import { useCallback } from "react";

import { addBugsnagBreadcrumb } from "@/react/helpers/bugsnagHelpers";

import { useGetStripeElements } from "../../useStripeElements";
import type { Attempt } from "./useCreateAttempt";

const paymentRecoveryActions = {
  navigate: "navigate",
  retry: "retry",
  cancel: "cancel",
} as const;

export type PaymentRecoveryAction =
  (typeof paymentRecoveryActions)[keyof typeof paymentRecoveryActions];

/**
 * After a confirmPayment error, retrieves the actual PaymentIntent status to
 * determine what recovery action to take.
 *
 * Returns:
 *   'navigate'  — payment complete (succeeded / requires_capture / processing)
 *   'retry'     — our call never landed (requires_confirmation), retry once
 *   'cancel'    — all other statuses or retrieval error, proceed with cancel
 */
export const useResolvePaymentRecoveryAction = () => {
  const getStripeElements = useGetStripeElements();

  return useCallback(
    async (attempt: Attempt): Promise<PaymentRecoveryAction> => {
      if (!attempt.paymentIntentClientSecret)
        return paymentRecoveryActions.cancel;
      try {
        const { stripe } = getStripeElements("useResolvePaymentRecoveryAction");
        const { paymentIntent, error: retrieveError } =
          await stripe.retrievePaymentIntent(attempt.paymentIntentClientSecret);
        const status = paymentIntent?.status;
        addBugsnagBreadcrumb(
          "useResolvePaymentRecoveryAction: PI status after confirm error",
          {
            metadata: { status, retrieveError },
            type: "manual",
          },
        );
        if (
          status === "succeeded" ||
          status === "requires_capture" ||
          status === "processing"
        )
          return paymentRecoveryActions.navigate;
        if (status === "requires_confirmation")
          return paymentRecoveryActions.retry;
      } catch {
        return paymentRecoveryActions.cancel;
      }
      return paymentRecoveryActions.cancel;
    },
    [getStripeElements],
  );
};
